import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Image, Download, Trash2, Heart, RefreshCw, Search, 
  Filter, X, Loader2, ImagePlus, Sparkles, Clock, Star,
  ChevronLeft, ChevronRight, Wand2, Edit3, History
} from 'lucide-react';
import { GlassCard } from '../components/layout/GlassCard';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import ImageEditor from '../components/image/ImageEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GalleryPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [fullImageData, setFullImageData] = useState(null);
  const [loadingFull, setLoadingFull] = useState(false);
  
  // Editor state
  const [showEditor, setShowEditor] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  
  // Versions state
  const [showVersions, setShowVersions] = useState(false);
  const [imageVersions, setImageVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStyle, setFilterStyle] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const limit = 12;

  // Regeneration
  const [regenerating, setRegenerating] = useState(null);

  const styles = [
    { id: '', name: 'All Styles' },
    { id: 'photorealistic', name: 'Photorealistic' },
    { id: 'artistic', name: 'Artistic' },
    { id: 'anime', name: 'Anime' },
    { id: 'digital-art', name: 'Digital Art' },
    { id: '3d-render', name: '3D Render' },
    { id: 'sketch', name: 'Sketch' }
  ];

  const fetchImages = useCallback(async (resetOffset = false) => {
    setLoading(true);
    try {
      const currentOffset = resetOffset ? 0 : offset;
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: currentOffset.toString()
      });
      
      if (searchQuery) params.append('search', searchQuery);
      if (filterStyle) params.append('style', filterStyle);
      if (favoritesOnly) params.append('favorites_only', 'true');
      
      const response = await axios.get(
        `${API_URL}/api/image/gallery?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setImages(response.data.images || []);
      setTotal(response.data.total || 0);
      setHasMore(response.data.has_more || false);
      if (resetOffset) setOffset(0);
    } catch (error) {
      console.error('Failed to fetch gallery:', error);
      toast.error('Failed to load gallery');
    } finally {
      setLoading(false);
    }
  }, [token, offset, searchQuery, filterStyle, favoritesOnly]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/image/gallery/stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchImages(true);
    fetchStats();
  }, [token, fetchImages, fetchStats]);

  useEffect(() => {
    fetchImages(true);
  }, [searchQuery, filterStyle, favoritesOnly, fetchImages]);

  const loadFullImage = async (imageId) => {
    setLoadingFull(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/image/gallery/${imageId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFullImageData(response.data);
    } catch (error) {
      toast.error('Failed to load image');
    } finally {
      setLoadingFull(false);
    }
  };

  const openImage = (image) => {
    setSelectedImage(image);
    loadFullImage(image.image_id);
  };

  const closeImage = () => {
    setSelectedImage(null);
    setFullImageData(null);
  };

  const downloadImage = (imageData, prompt) => {
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${imageData}`;
    link.download = `aira-${prompt.slice(0, 30).replace(/[^a-z0-9]/gi, '-')}-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Image downloaded!');
  };

  const toggleFavorite = async (imageId, e) => {
    e?.stopPropagation();
    try {
      const response = await axios.post(
        `${API_URL}/api/image/gallery/${imageId}/favorite`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setImages(prev => prev.map(img => 
        img.image_id === imageId 
          ? { ...img, is_favorite: response.data.is_favorite }
          : img
      ));
      
      if (selectedImage?.image_id === imageId) {
        setSelectedImage(prev => ({ ...prev, is_favorite: response.data.is_favorite }));
      }
      
      fetchStats();
    } catch (error) {
      toast.error('Failed to update favorite');
    }
  };

  const deleteImage = async (imageId, e) => {
    e?.stopPropagation();
    if (!confirm('Are you sure you want to delete this image?')) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/image/gallery/${imageId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setImages(prev => prev.filter(img => img.image_id !== imageId));
      setTotal(prev => prev - 1);
      
      if (selectedImage?.image_id === imageId) {
        closeImage();
      }
      
      fetchStats();
      toast.success('Image deleted');
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const regenerateImage = async (imageId) => {
    setRegenerating(imageId);
    try {
      const response = await axios.post(
        `${API_URL}/api/image/regenerate`,
        { image_id: imageId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Image regenerated!');
        fetchImages(true);
        fetchStats();
        closeImage();
      }
    } catch (error) {
      toast.error('Failed to regenerate image');
    } finally {
      setRegenerating(null);
    }
  };

  // Open image editor
  const openEditor = () => {
    if (fullImageData) {
      setEditingImage(fullImageData);
      setShowEditor(true);
    }
  };

  // Handle edit complete
  const handleEditComplete = (result) => {
    fetchImages(true);
    fetchStats();
  };

  // Fetch image versions
  const fetchVersions = async (imageId) => {
    setLoadingVersions(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/image/gallery/${imageId}/versions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setImageVersions(response.data.versions || []);
      setShowVersions(true);
    } catch (error) {
      toast.error('Failed to load versions');
    } finally {
      setLoadingVersions(false);
    }
  };

  const nextPage = () => {
    if (hasMore) {
      setOffset(prev => prev + limit);
      fetchImages();
    }
  };

  const prevPage = () => {
    if (offset > 0) {
      setOffset(prev => Math.max(0, prev - limit));
      fetchImages();
    }
  };

  return (
    <div className="max-w-7xl mx-auto" data-testid="gallery-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold mb-2">
            <span className="bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] bg-clip-text text-transparent">
              My Creations
            </span>
          </h1>
          <p className="text-[var(--text-secondary)]">Your AI-generated image gallery</p>
        </div>
        
        <button
          onClick={() => navigate('/creative')}
          className="btn-primary flex items-center gap-2"
          data-testid="create-new-btn"
        >
          <ImagePlus className="w-5 h-5" />
          Create New Image
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <GlassCard className="p-4 text-center">
            <div className="w-10 h-10 rounded-xl bg-[var(--color-primary)]/20 flex items-center justify-center mx-auto mb-2">
              <Image className="w-5 h-5 text-[var(--color-primary)]" />
            </div>
            <p className="text-2xl font-bold text-white">{stats.total_images}</p>
            <p className="text-xs text-[var(--text-tertiary)]">Total Images</p>
          </GlassCard>
          
          <GlassCard className="p-4 text-center">
            <div className="w-10 h-10 rounded-xl bg-pink-500/20 flex items-center justify-center mx-auto mb-2">
              <Heart className="w-5 h-5 text-pink-400" />
            </div>
            <p className="text-2xl font-bold text-white">{stats.favorites_count}</p>
            <p className="text-xs text-[var(--text-tertiary)]">Favorites</p>
          </GlassCard>
          
          <GlassCard className="p-4 text-center">
            <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center mx-auto mb-2">
              <Clock className="w-5 h-5 text-green-400" />
            </div>
            <p className="text-2xl font-bold text-white">{stats.images_this_month}</p>
            <p className="text-xs text-[var(--text-tertiary)]">This Month</p>
          </GlassCard>
          
          <GlassCard className="p-4 text-center">
            <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center mx-auto mb-2">
              <Star className="w-5 h-5 text-purple-400" />
            </div>
            <p className="text-2xl font-bold text-white capitalize">{stats.most_used_style || 'None'}</p>
            <p className="text-xs text-[var(--text-tertiary)]">Top Style</p>
          </GlassCard>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-tertiary)]" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by prompt..."
            className="input-field w-full pl-12"
            data-testid="gallery-search"
          />
        </div>
        
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
            showFilters || filterStyle || favoritesOnly
              ? 'bg-[var(--color-primary)]/20 text-[var(--color-primary)] border border-[var(--color-primary)]/50'
              : 'bg-white/5 text-[var(--text-secondary)] hover:bg-white/10'
          }`}
        >
          <Filter className="w-5 h-5" />
          Filters
          {(filterStyle || favoritesOnly) && (
            <span className="w-2 h-2 rounded-full bg-[var(--color-primary)]" />
          )}
        </button>
      </div>

      {/* Filter Options */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden mb-6"
          >
            <GlassCard className="p-4">
              <div className="flex flex-wrap gap-4">
                <div>
                  <label className="text-xs text-[var(--text-tertiary)] mb-2 block">Style</label>
                  <div className="flex flex-wrap gap-2">
                    {styles.map(style => (
                      <button
                        key={style.id}
                        onClick={() => setFilterStyle(style.id)}
                        className={`px-3 py-1.5 text-sm rounded-lg transition-all ${
                          filterStyle === style.id
                            ? 'bg-[var(--color-primary)]/30 text-white border border-[var(--color-primary)]/50'
                            : 'bg-white/5 text-[var(--text-secondary)] hover:bg-white/10'
                        }`}
                      >
                        {style.name}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="text-xs text-[var(--text-tertiary)] mb-2 block">Show</label>
                  <button
                    onClick={() => setFavoritesOnly(!favoritesOnly)}
                    className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-all ${
                      favoritesOnly
                        ? 'bg-pink-500/30 text-white border border-pink-500/50'
                        : 'bg-white/5 text-[var(--text-secondary)] hover:bg-white/10'
                    }`}
                  >
                    <Heart className={`w-4 h-4 ${favoritesOnly ? 'fill-current' : ''}`} />
                    Favorites Only
                  </button>
                </div>
              </div>
            </GlassCard>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Gallery Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
        </div>
      ) : images.length === 0 ? (
        <GlassCard className="p-12 text-center">
          <div className="w-20 h-20 rounded-full bg-gradient-to-r from-[var(--color-primary)]/20 to-[var(--color-secondary)]/20 flex items-center justify-center mx-auto mb-6">
            <ImagePlus className="w-10 h-10 text-[var(--color-primary)]" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">No images yet</h3>
          <p className="text-[var(--text-secondary)] mb-6">
            {searchQuery || filterStyle || favoritesOnly
              ? 'No images match your filters. Try adjusting your search.'
              : 'Start creating AI-generated images to build your gallery!'}
          </p>
          <button
            onClick={() => navigate('/creative')}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Sparkles className="w-5 h-5" />
            Create Your First Image
          </button>
        </GlassCard>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((image, index) => (
              <motion.div
                key={image.image_id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05 }}
                className="relative group cursor-pointer"
                onClick={() => openImage(image)}
                data-testid={`gallery-image-${index}`}
              >
                <div className="aspect-square rounded-xl overflow-hidden bg-white/5 border border-white/10">
                  <img
                    src={`data:${image.mime_type || 'image/png'};base64,${image.thumbnail_data}`}
                    alt={image.prompt}
                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                  />
                </div>
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-xl">
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <p className="text-sm text-white line-clamp-2 mb-2">{image.prompt}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400 capitalize">{image.style || 'auto'}</span>
                      <div className="flex gap-2">
                        <button
                          onClick={(e) => toggleFavorite(image.image_id, e)}
                          className={`p-1.5 rounded-lg transition-colors ${
                            image.is_favorite
                              ? 'bg-pink-500/30 text-pink-400'
                              : 'bg-white/10 text-white hover:bg-white/20'
                          }`}
                        >
                          <Heart className={`w-4 h-4 ${image.is_favorite ? 'fill-current' : ''}`} />
                        </button>
                        <button
                          onClick={(e) => deleteImage(image.image_id, e)}
                          className="p-1.5 rounded-lg bg-white/10 text-white hover:bg-red-500/30 hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Favorite badge */}
                {image.is_favorite && (
                  <div className="absolute top-2 right-2">
                    <Heart className="w-5 h-5 text-pink-400 fill-current" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-8">
            <p className="text-sm text-[var(--text-tertiary)]">
              Showing {offset + 1}-{Math.min(offset + limit, total)} of {total} images
            </p>
            <div className="flex gap-2">
              <button
                onClick={prevPage}
                disabled={offset === 0}
                className="p-2 rounded-lg bg-white/5 text-[var(--text-secondary)] hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={nextPage}
                disabled={!hasMore}
                className="p-2 rounded-lg bg-white/5 text-[var(--text-secondary)] hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Image Detail Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={closeImage}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-4xl w-full bg-[var(--bg-card)] rounded-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={closeImage}
                className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="grid md:grid-cols-2">
                {/* Image */}
                <div className="relative bg-black/20">
                  {loadingFull ? (
                    <div className="aspect-square flex items-center justify-center">
                      <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
                    </div>
                  ) : fullImageData ? (
                    <img
                      src={`data:${fullImageData.mime_type || 'image/png'};base64,${fullImageData.image_data}`}
                      alt={selectedImage.prompt}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <img
                      src={`data:${selectedImage.mime_type || 'image/png'};base64,${selectedImage.thumbnail_data}`}
                      alt={selectedImage.prompt}
                      className="w-full h-full object-contain"
                    />
                  )}
                </div>

                {/* Details */}
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Image Details</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs text-[var(--text-tertiary)] uppercase tracking-wide">Prompt</label>
                      <p className="text-[var(--text-secondary)] mt-1">{selectedImage.prompt}</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-[var(--text-tertiary)] uppercase tracking-wide">Style</label>
                        <p className="text-white mt-1 capitalize">{selectedImage.style || 'Auto'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-[var(--text-tertiary)] uppercase tracking-wide">Aspect Ratio</label>
                        <p className="text-white mt-1">{selectedImage.aspect_ratio || '1:1'}</p>
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-xs text-[var(--text-tertiary)] uppercase tracking-wide">Created</label>
                      <p className="text-white mt-1">
                        {new Date(selectedImage.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t border-white/10">
                    <button
                      onClick={() => fullImageData && downloadImage(fullImageData.image_data, selectedImage.prompt)}
                      disabled={!fullImageData}
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50 transition-all"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </button>
                    
                    <button
                      onClick={() => toggleFavorite(selectedImage.image_id)}
                      className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl transition-all ${
                        selectedImage.is_favorite
                          ? 'bg-pink-500/20 text-pink-400 border border-pink-500/50'
                          : 'bg-white/5 text-[var(--text-secondary)] hover:bg-white/10'
                      }`}
                    >
                      <Heart className={`w-4 h-4 ${selectedImage.is_favorite ? 'fill-current' : ''}`} />
                    </button>
                    
                    <button
                      onClick={() => regenerateImage(selectedImage.image_id)}
                      disabled={regenerating === selectedImage.image_id}
                      className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 text-[var(--text-secondary)] hover:bg-white/10 disabled:opacity-50 transition-all"
                    >
                      {regenerating === selectedImage.image_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                      Regenerate
                    </button>
                  </div>

                  {/* Edit Image Button */}
                  <button
                    onClick={openEditor}
                    disabled={!fullImageData}
                    className="w-full flex items-center justify-center gap-2 mt-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-white border border-purple-500/30 hover:border-purple-500/50 disabled:opacity-50 transition-all"
                    data-testid="edit-image-btn"
                  >
                    <Edit3 className="w-4 h-4" />
                    Edit Image
                  </button>

                  {/* View Versions Button */}
                  <button
                    onClick={() => fetchVersions(selectedImage.image_id)}
                    disabled={loadingVersions}
                    className="w-full flex items-center justify-center gap-2 mt-2 py-2.5 rounded-xl bg-white/5 text-[var(--text-secondary)] hover:bg-white/10 disabled:opacity-50 transition-all"
                  >
                    {loadingVersions ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <History className="w-4 h-4" />
                    )}
                    View Edit History
                  </button>

                  {/* Use in Creative Studio */}
                  <button
                    onClick={() => {
                      navigate('/creative', { state: { prompt: selectedImage.prompt, style: selectedImage.style } });
                    }}
                    className="w-full flex items-center justify-center gap-2 mt-2 py-2.5 rounded-xl bg-gradient-to-r from-[var(--color-primary)]/20 to-[var(--color-secondary)]/20 text-white border border-white/10 hover:border-white/20 transition-all"
                  >
                    <Wand2 className="w-4 h-4" />
                    Use Prompt in Creative Studio
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Image Editor Modal */}
      <ImageEditor
        isOpen={showEditor}
        onClose={() => {
          setShowEditor(false);
          setEditingImage(null);
        }}
        image={editingImage}
        onEditComplete={handleEditComplete}
      />

      {/* Version History Modal */}
      <AnimatePresence>
        {showVersions && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setShowVersions(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-2xl w-full bg-[var(--bg-card)] rounded-2xl overflow-hidden max-h-[80vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <History className="w-5 h-5 text-[var(--color-primary)]" />
                  <h3 className="text-lg font-semibold text-white">Edit History</h3>
                </div>
                <button
                  onClick={() => setShowVersions(false)}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5 text-[var(--text-secondary)]" />
                </button>
              </div>
              
              <div className="p-4 overflow-y-auto max-h-[60vh]">
                {imageVersions.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-[var(--text-secondary)]">No edit history found</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {imageVersions.map((version, index) => (
                      <div
                        key={version.image_id}
                        className={`p-4 rounded-xl border ${
                          version.is_original
                            ? 'bg-[var(--color-primary)]/10 border-[var(--color-primary)]/30'
                            : 'bg-white/5 border-white/10'
                        }`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-16 h-16 rounded-lg overflow-hidden bg-black/20 flex-shrink-0">
                            <img
                              src={`data:${version.mime_type || 'image/png'};base64,${version.thumbnail_data}`}
                              alt={`Version ${version.version}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-white">
                                {version.is_original ? 'Original' : `Version ${version.version}`}
                              </span>
                              {version.is_original && (
                                <span className="px-2 py-0.5 text-xs rounded-full bg-[var(--color-primary)]/20 text-[var(--color-primary)]">
                                  Original
                                </span>
                              )}
                            </div>
                            {version.edit_operation && (
                              <p className="text-sm text-[var(--text-secondary)] mt-1 capitalize">
                                {version.edit_operation.replace('_', ' ')}
                              </p>
                            )}
                            <p className="text-xs text-[var(--text-tertiary)] mt-1">
                              {new Date(version.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
